import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const WEEK_DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const CAT_COLORS = {
  wissen: "rgba(140,138,106,0.30)",
  rezept: "rgba(203,163,110,0.30)",
  saison: "rgba(180,160,80,0.30)",
  sicherheit: "rgba(167,58,58,0.22)",
  luxemburg: "rgba(95,91,62,0.30)",
};

// Build a Monday-first 6-row x 7-col date grid covering the month
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // 0=Mo … 6=So
  const start = new Date(year, month, 1 - startWeekday);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function articleDate(a) {
  return (a.scheduled_at || a.created_at || "").slice(0, 10);
}

function statusFor(a) {
  if (a.scheduled_at) {
    const inFuture = a.scheduled_at > new Date().toISOString();
    if (!a.published) return { label: "Entwurf", color: "rgba(0,0,0,0.10)" };
    return inFuture
      ? { label: "geplant", color: "rgba(140,138,106,0.30)" }
      : { label: "live", color: "rgba(140,138,106,0.55)" };
  }
  if (!a.published) return { label: "Entwurf", color: "rgba(0,0,0,0.10)" };
  return { label: "live", color: "rgba(140,138,106,0.55)" };
}

export default function ArticlesCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null); // article being dragged
  const [editScheduleFor, setEditScheduleFor] = useState(null); // article for time-picker dialog

  const load = () => {
    setLoading(true);
    api.get("/admin/articles/calendar").then((r) => setArticles(r.data?.articles || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const articlesByDay = useMemo(() => {
    const map = {};
    for (const a of articles) {
      const d = articleDate(a);
      if (!d) continue;
      (map[d] = map[d] || []).push(a);
    }
    return map;
  }, [articles]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear()); setMonth(today.getMonth());
  };

  const onDragStart = (a) => setDragging(a);
  const onDragOver = (e) => { e.preventDefault(); };

  const onDrop = async (dateStr) => {
    if (!dragging) return;
    const a = dragging;
    setDragging(null);
    // Preserve time of day if scheduled_at has one; default 09:00 UTC.
    let isoTime = "T09:00:00.000Z";
    if (a.scheduled_at) {
      const m = /T(\d{2}:\d{2}:\d{2}\.?\d*Z?)/.exec(a.scheduled_at);
      if (m) isoTime = "T" + m[1];
    }
    const newSched = `${dateStr}${isoTime.replace(/Z?$/, "Z")}`;
    try {
      await api.put(`/admin/articles/${a.id}/schedule`, { scheduled_at: newSched });
      toast.success(`«${a.title}" verschoben auf ${dateStr}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verschieben fehlgeschlagen");
    }
  };

  const clearSchedule = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}/schedule`, { scheduled_at: null });
      toast.success("Planung gelöscht – Artikel ist jetzt sofort sichtbar (falls veröffentlicht)");
      load();
      setEditScheduleFor(null);
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const saveSchedule = async (a, dateStr, timeStr) => {
    const iso = `${dateStr}T${timeStr}:00.000Z`;
    try {
      await api.put(`/admin/articles/${a.id}/schedule`, { scheduled_at: iso });
      toast.success("Planung gespeichert");
      load();
      setEditScheduleFor(null);
    } catch (err) {
      toast.error("Fehler");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="articles-calendar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 rounded-full hover:bg-[var(--wp-cream)]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display text-2xl" style={{ color: "var(--wp-olive)" }} data-testid="cal-title">
            {MONTH_NAMES[month]} {year}
          </div>
          <button onClick={nextMonth} className="p-2 rounded-full hover:bg-[var(--wp-cream)]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={goToday} className="ml-2 px-3 py-1.5 rounded-full text-xs border" style={{ borderColor: "var(--wp-border)" }} data-testid="cal-today">Heute</button>
        </div>
        <div className="text-xs opacity-70 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "rgba(140,138,106,0.55)" }} /> Live</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "rgba(140,138,106,0.30)" }} /> Geplant</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "rgba(0,0,0,0.10)" }} /> Entwurf</span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px rounded-xl overflow-hidden border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-border)" }}>
        {WEEK_DAYS.map((d) => (
          <div key={d} className="text-xs font-medium px-3 py-2" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>{d}</div>
        ))}
        {grid.map((d, idx) => {
          const dateStr = toISODate(d);
          const isCurrentMonth = d.getMonth() === month;
          const isToday = toISODate(today) === dateStr;
          const items = articlesByDay[dateStr] || [];
          return (
            <div
              key={idx}
              onDragOver={onDragOver}
              onDrop={() => onDrop(dateStr)}
              className="min-h-[110px] p-1.5 text-xs"
              style={{
                backgroundColor: isCurrentMonth ? "white" : "rgba(231,222,208,0.5)",
                opacity: isCurrentMonth ? 1 : 0.55,
              }}
              data-testid={`cal-cell-${dateStr}`}
            >
              <div className={`text-[10px] mb-1 ${isToday ? "font-semibold" : "opacity-60"}`} style={isToday ? { color: "var(--wp-olive)" } : {}}>
                {d.getDate()}
              </div>
              <div className="space-y-1">
                {items.map((a) => {
                  const st = statusFor(a);
                  return (
                    <div
                      key={a.id}
                      draggable
                      onDragStart={() => onDragStart(a)}
                      onClick={() => setEditScheduleFor(a)}
                      className="px-1.5 py-1 rounded text-[10px] leading-tight cursor-move truncate"
                      style={{ backgroundColor: st.color, color: "var(--wp-forest)" }}
                      title={`${a.title} (${st.label})`}
                      data-testid={`cal-article-${a.slug}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: CAT_COLORS[a.category] || "rgba(0,0,0,0.15)" }} />
                      {a.title}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs opacity-60 mt-3">
        Tipp: Ziehe einen Artikel per Drag&amp;Drop auf einen anderen Tag, um die Veröffentlichung zu verschieben. Klick öffnet den Termin-Dialog.
      </p>

      {editScheduleFor && (
        <ScheduleDialog
          article={editScheduleFor}
          onClose={() => setEditScheduleFor(null)}
          onSave={saveSchedule}
          onClear={clearSchedule}
        />
      )}
    </div>
  );
}

function ScheduleDialog({ article, onClose, onSave, onClear }) {
  const initial = article.scheduled_at || `${new Date().toISOString().slice(0, 10)}T09:00:00.000Z`;
  const [date, setDate] = useState(initial.slice(0, 10));
  const [time, setTime] = useState((initial.slice(11, 16)) || "09:00");

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(58,57,44,0.65)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline mb-1">Planung</div>
            <h3 className="font-display text-xl" style={{ color: "var(--wp-olive)" }}>{article.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--wp-cream)]"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3 text-sm">
          <label className="block">
            <div className="overline mb-1">Veröffentlichungsdatum</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="schedule-date" />
          </label>
          <label className="block">
            <div className="overline mb-1">Uhrzeit (UTC)</div>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="schedule-time" />
          </label>
          <p className="text-xs opacity-60">
            Bei Veröffentlichung zur geplanten Zeit wird der Artikel automatisch öffentlich. Bis dahin sehen nur Admins ihn.
          </p>
        </div>

        <div className="flex justify-between items-center gap-2 mt-5">
          {article.scheduled_at ? (
            <button type="button" onClick={() => onClear(article)} className="text-xs underline opacity-70" data-testid="schedule-clear">
              Planung löschen
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
            <button type="button" onClick={() => onSave(article, date, time)} className="btn-primary" data-testid="schedule-save">Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}
